import { useMemo, useEffect, useCallback } from 'react';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import { deepCompare } from '@/utils/deepCompare';
import useAppModal from '@/hooks/useAppModal';
import { FORM_MESSAGES } from '@/constants/formMessages';
import { useFormConfirmationBlocker, authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';

/**
 * Centralized hook for form change detection, global registration, and guarded actions.
 *
 * Combines four concerns into a single hook:
 * 1. **Detection** — deep-compares currentData vs initialData
 * 2. **Registration** — syncs the dirty flag to global state (enables beforeunload warning)
 * 3. **Guard** — `confirmIfChanged(cb)` shows a confirm dialog before destructive actions
 * 4. **Navigation block** — uses React Router `useBlocker` to intercept back/link navigation
 *
 * @example
 * const { hasFormChanged, confirmIfChanged, clearFormChange } = useFormChangeGuard(formData, initialFormData);
 *
 * const handleClose = () => confirmIfChanged(() => { clearFormChange(); onClose(); });
 * const handleClear = () => confirmIfChanged(() => { resetForm(); });
 * // After successful save:
 * clearFormChange();
 */
export const useFormChangeGuard = <T>(currentData: T, initialData: T | null | undefined) => {
  const { formChangeState, registerFormChange, clearFormChange } = useGlobalBehavior();
  const { confirm } = useAppModal();

  const hasFormChanged = useMemo(() => {
    if (initialData == null || currentData == null) return false;
    return !deepCompare(initialData, currentData);
  }, [currentData, initialData]);

  useEffect(() => {
    registerFormChange(hasFormChanged);
    return () => clearFormChange();
  }, [hasFormChanged, registerFormChange, clearFormChange]);

  // Reuse the common blocker + confirmation logic
  useFormConfirmationBlocker(hasFormChanged, clearFormChange);

  const confirmIfChanged = useCallback(
    (onConfirm: () => void) => {
      if (hasFormChanged) {
        confirm({
          title: FORM_MESSAGES.DISCARD_CHANGES_TITLE,
          content: FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION,
          okText: FORM_MESSAGES.CONFIRM_OK,
          cancelText: FORM_MESSAGES.CONFIRM_CANCEL,
          okButtonProps: { danger: true },
          onOk: () => {
            // Mark the next navigation as authorized so the router blocker
            // does not show a second window.confirm on top of this dialog.
            authorizeNavigation();
            onConfirm();
          },
        });
      } else {
        onConfirm();
      }
    },
    [hasFormChanged, confirm, currentData, initialData, formChangeState]
  );

  return { hasFormChanged, confirmIfChanged, clearFormChange, formChangeState };
};

/**
 * Same as useFormChangeGuard but without React Router navigation blocker.
 * Use when the form is inside a modal that's already nested within a guarded page
 * (React Router only supports one useBlocker at a time).
 */
export const useFormChangeGuardWithoutBlocker = <T>(currentData: T, initialData: T | null | undefined) => {
  const { formChangeState, registerFormChange, clearFormChange } = useGlobalBehavior();
  const { confirm } = useAppModal();

  const hasFormChanged = useMemo(() => {
    if (initialData == null || currentData == null) return false;
    return !deepCompare(initialData, currentData);
  }, [currentData, initialData]);

  useEffect(() => {
    registerFormChange(hasFormChanged);
    return () => clearFormChange();
  }, [hasFormChanged, registerFormChange, clearFormChange]);

  // No useFormConfirmationBlocker — intentionally omitted

  const confirmIfChanged = useCallback(
    (onConfirm: () => void) => {
      if (hasFormChanged) {
        confirm({
          title: FORM_MESSAGES.DISCARD_CHANGES_TITLE,
          content: FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION,
          okText: FORM_MESSAGES.CONFIRM_OK,
          cancelText: FORM_MESSAGES.CONFIRM_CANCEL,
          okButtonProps: { danger: true },
          onOk: () => {
            onConfirm();
          },
        });
      } else {
        onConfirm();
      }
    },
    [hasFormChanged, confirm, currentData, initialData, formChangeState]
  );

  return { hasFormChanged, confirmIfChanged, clearFormChange, formChangeState };
};
